import { Chip } from '@mui/material';
import {
  CreateButton,
  Datagrid,
  FilterButton,
  FunctionField,
  List,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  TextField,
  TextInput,
  TopToolbar,
  useRecordContext,
  type RaRecord,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';

/**
 * Charities list — the first Phase-2 "tail" entity surface and the first with
 * CREATE. Charities are first-class and LIVE (cashout donations reference them),
 * so they are managed, not just listed: list / detail / create / edit, with a
 * DEACTIVATE-not-delete model (D-charity-softdelete) — there is no delete
 * anywhere.
 *
 * Region-scoped by the switcher like the other lists (a charity belongs to one
 * market); filter by charity type and active status; `q` matches the name.
 */

/** Active/inactive as a chip — the soft-disable state (D-charity-softdelete). */
export const ActiveChip = () => {
  const record = useRecordContext();
  const active = record?.status === 'active';
  return (
    <Chip
      size="small"
      label={active ? 'Active' : 'Inactive'}
      color={active ? 'success' : 'default'}
      variant={active ? 'filled' : 'outlined'}
    />
  );
};

const charityFilters = [
  <TextInput key="q" source="q" label="Search" alwaysOn resettable />,
  <ReferenceInput
    key="charityTypeId"
    source="charityTypeId"
    reference="charityTypes"
    sort={{ field: 'label', order: 'ASC' }}
    perPage={100}
  >
    <SelectInput label="Charity type" optionText="label" />
  </ReferenceInput>,
  <SelectInput
    key="status"
    source="status"
    label="Status"
    choices={[
      { id: 'active', name: 'Active' },
      { id: 'inactive', name: 'Inactive' },
    ]}
  />,
];

// Create is gated charities:write, so the affordance is hidden from a viewer
// (the route is guarded too — see CharityCreate).
const CharityListActions = () => {
  const { role, accessibleRegions } = useAccessContext();
  const canWrite = hasAccess(
    { role, accessibleRegions },
    { capability: 'charities:write' },
  );
  return (
    <TopToolbar>
      <FilterButton />
      {canWrite && <CreateButton />}
    </TopToolbar>
  );
};

/** The list itself. Exported for testing without the access wrapper. */
export const CharityListView = () => {
  const { activeRegion } = useRegion();
  return (
    <List
      resource="charities"
      title="Charities"
      filter={{ region: activeRegion }}
      filters={charityFilters}
      actions={<CharityListActions />}
      sort={{ field: 'name', order: 'ASC' }}
      perPage={25}
      exporter={false}
    >
      {/* Clicking a row opens the charity detail. No bulk actions, and crucially
          no bulk delete — charities are deactivated, never deleted. */}
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField source="name" label="Name" />
        <FunctionField
          label="Market"
          sortBy="market"
          render={(record: RaRecord) => (record.market as string).toUpperCase()}
        />
        <ReferenceField
          label="Charity type"
          source="charityTypeId"
          reference="charityTypes"
          sortable={false}
          link={false}
        >
          <TextField source="label" />
        </ReferenceField>
        <FunctionField
          label="Status"
          sortBy="status"
          render={() => <ActiveChip />}
        />
      </Datagrid>
    </List>
  );
};

/** Gated entry point registered as the `charities` Resource's list. */
export const CharityList = () => (
  <RequireAccess capability="charities:read">
    <CharityListView />
  </RequireAccess>
);
